import { useState, useRef, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, X, ChevronUp, ChevronDown } from 'lucide-react';
import { usePodcastPlayer } from './podcast/PodcastPlayerContext';

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function PodcastPlayer() {
  const {
    episode,
    isPlaying,
    currentTime,
    duration,
    playbackRate,
    volume,
    isVisible,
    togglePlay,
    skipForward,
    skipBackward,
    setPlaybackRate,
    setVolume,
    seek,
    close,
  } = usePodcastPlayer();

  const [isMini, setIsMini] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const [showRateMenu, setShowRateMenu] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const bar = progressRef.current;
    if (!bar || !duration) return;
    const rect = bar.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seek(fraction * duration);
  }, [duration, seek]);

  const handleProgressDrag = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.buttons !== 1) return;
    handleProgressClick(e);
  }, [handleProgressClick]);

  if (!isVisible || !episode) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (isMini) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-9999 bg-[#16161e] border-t border-white/8 px-4 py-2">
        {/* Mini progress bar */}
        <div
          ref={progressRef}
          className="absolute top-0 left-0 right-0 h-1 bg-white/10 cursor-pointer"
          onClick={handleProgressClick}
          onMouseMove={handleProgressDrag}
        >
          <div className="h-full bg-iris-500 transition-[width] duration-100" style={{ width: `${progress}%` }} />
        </div>

        <div className="flex items-center gap-3">
          {episode.artworkUrl && (
            <img
              src={episode.artworkUrl}
              alt=""
              className="w-8 h-8 rounded object-cover shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{episode.title}</p>
            <p className="text-xs text-white/50 truncate">{episode.podcastTitle}</p>
          </div>
          <button onClick={togglePlay} className="p-1.5 text-white hover:text-iris-400 transition-colors">
            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
          </button>
          <button onClick={() => setIsMini(false)} className="p-1 text-white/40 hover:text-white transition-colors">
            <ChevronUp size={16} />
          </button>
          <button onClick={close} className="p-1 text-white/40 hover:text-red-400 transition-colors">
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-9999 bg-[#16161e] border-t border-white/8 shadow-2xl">
      {/* Progress bar */}
      <div
        ref={progressRef}
        className="relative h-1.5 bg-white/10 cursor-pointer group"
        onClick={handleProgressClick}
        onMouseMove={handleProgressDrag}
      >
        <div
          className="h-full bg-iris-500 transition-[width] duration-100"
          style={{ width: `${progress}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-iris-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: `calc(${progress}% - 6px)` }}
        />
      </div>

      <div className="px-4 py-3 flex items-center gap-4">
        {/* Episode info */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {episode.artworkUrl && (
            <img
              src={episode.artworkUrl}
              alt=""
              className="w-12 h-12 rounded-lg object-cover shrink-0"
            />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{episode.title}</p>
            <p className="text-xs text-white/50 truncate">{episode.podcastTitle}</p>
          </div>
        </div>

        {/* Playback controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => skipBackward(15)}
            className="p-2 text-white/70 hover:text-white transition-colors"
            title="Skip back 15s"
          >
            <SkipBack size={18} />
          </button>

          <button
            onClick={togglePlay}
            className="p-2.5 bg-iris-600 hover:bg-iris-500 text-[#0C0C12] rounded-full transition-colors"
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
          </button>

          <button
            onClick={() => skipForward(15)}
            className="p-2 text-white/70 hover:text-white transition-colors"
            title="Skip forward 15s"
          >
            <SkipForward size={18} />
          </button>
        </div>

        {/* Time display */}
        <div className="text-xs text-white/50 tabular-nums whitespace-nowrap">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        {/* Playback speed */}
        <div className="relative">
          <button
            onClick={() => setShowRateMenu(!showRateMenu)}
            className="px-2 py-1 text-xs font-medium text-white/70 hover:text-white bg-white/5 hover:bg-white/10 rounded transition-colors"
          >
            {playbackRate}x
          </button>
          {showRateMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowRateMenu(false)} />
              <div className="absolute bottom-full right-0 mb-2 bg-[#1e1e2a] border border-white/8 rounded-lg shadow-xl py-1 z-20">
                {PLAYBACK_RATES.map(rate => (
                  <button
                    key={rate}
                    onClick={() => { setPlaybackRate(rate); setShowRateMenu(false); }}
                    className={`block w-full px-4 py-1.5 text-sm text-left hover:bg-white/5 transition-colors ${
                      rate === playbackRate ? 'text-iris-400' : 'text-white/70'
                    }`}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Volume */}
        <div className="relative flex items-center gap-1">
          <button
            onClick={() => setShowVolume(!showVolume)}
            className="p-1.5 text-white/70 hover:text-white transition-colors"
          >
            {volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          {showVolume && (
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-20 h-1 accent-iris-500 cursor-pointer"
            />
          )}
        </div>

        {/* Minimize / Close */}
        <button
          onClick={() => setIsMini(true)}
          className="p-1 text-white/40 hover:text-white transition-colors"
          title="Minimize"
        >
          <ChevronDown size={16} />
        </button>
        <button
          onClick={close}
          className="p-1 text-white/40 hover:text-red-400 transition-colors"
          title="Close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
